import { Chip, Tooltip } from '@mui/material';
import { emphasize, styled } from '@mui/material/styles';
import {
  type ComponentType,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

/**
 * Copy text to clipboard using modern API with legacy fallback.
 * Returns true if copy succeeded, false otherwise.
 */
async function copyToClipboard(text: string): Promise<boolean> {
  // Try modern Clipboard API first
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to legacy method
    }
  }

  // Legacy fallback using execCommand
  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.style.position = 'fixed';
  textArea.style.left = '-9999px';
  textArea.style.top = '-9999px';
  document.body.appendChild(textArea);

  try {
    textArea.focus();
    textArea.select();
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(textArea);
  }
}

const StyledCopyableBreadcrumb = styled(Chip)(({ theme }) => {
  const backgroundColor =
    theme.palette.mode === 'light'
      ? theme.palette.background.default
      : theme.palette.grey[800];
  return {
    backgroundColor,
    height: theme.spacing(3),
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: theme.typography.subtitle1.fontSize,
    cursor: 'pointer',
    '&:hover, &:focus': {
      backgroundColor: emphasize(backgroundColor, 0.06),
    },
    '&:active': {
      boxShadow: theme.shadows[1],
      backgroundColor: emphasize(backgroundColor, 0.12),
    },
    '&:focus-visible': {
      outline: `4px solid ${theme.palette.primary.main}`,
      outlineOffset: '-1px',
    },
  };
}) as typeof Chip;

const VisuallyHidden = styled('span')({
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
});

export interface CopyableBreadcrumbItemProps {
  /** Text to display and copy */
  label: string;
  /** Optional icon component to render */
  icon?: ComponentType<{ fontSize: 'small' | 'inherit' }>;
  /** Delay in ms before tooltip auto-hides (default: 1500) */
  tooltipAutoHideDelay?: number;
  /** Whether this is the current page in breadcrumb navigation (default: true) */
  isCurrentPage?: boolean;
}

/**
 * A breadcrumb item that copies its label to the clipboard when clicked.
 *
 * Features:
 * - Click or Enter/Space to copy
 * - "Copied!" tooltip feedback
 * - Full keyboard accessibility
 * - Screen reader announcements
 * - Graceful error handling
 */
const CopyableBreadcrumbItem = memo(function CopyableBreadcrumbItem({
  label,
  icon: Icon,
  tooltipAutoHideDelay = 1500,
  isCurrentPage = true,
}: CopyableBreadcrumbItemProps) {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const copyCountRef = useRef(0);
  const isMountedRef = useRef(true);

  // Track mount status and cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleCopy = useCallback(async () => {
    const success = await copyToClipboard(label);

    // Bail out if component unmounted during async operation
    if (!isMountedRef.current) return;

    if (!success) {
      console.error('Failed to copy to clipboard');
      return;
    }

    setTooltipOpen(true);

    // Increment counter and use invisible spaces to ensure unique announcement
    // for screen readers on rapid clicks (same text won't trigger re-announcement)
    copyCountRef.current += 1;
    const invisibleMarker = '\u200B'.repeat(copyCountRef.current % 10);
    setAnnouncement(`Copied to clipboard${invisibleMarker}`);

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Auto-hide tooltip after delay
    timeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        setTooltipOpen(false);
        setAnnouncement('');
      }
    }, tooltipAutoHideDelay);
  }, [label, tooltipAutoHideDelay]);

  // Note: No custom onKeyDown needed - MUI Chip handles Enter/Space internally
  // and triggers onClick, providing keyboard accessibility out of the box

  return (
    <>
      <Tooltip title="Copied!" open={tooltipOpen} arrow placement="top">
        <StyledCopyableBreadcrumb
          size="medium"
          label={label}
          icon={Icon ? <Icon fontSize="small" /> : undefined}
          onClick={handleCopy}
          role="button"
          tabIndex={0}
          aria-label={`Copy ${label} to clipboard`}
          aria-current={isCurrentPage ? 'page' : undefined}
        />
      </Tooltip>
      {/* Screen reader announcement */}
      <VisuallyHidden aria-live="polite" aria-atomic="true">
        {announcement}
      </VisuallyHidden>
    </>
  );
});

export default CopyableBreadcrumbItem;
